import React from 'react';
import { ListItem, ListItemText } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { NavMenuItemType } from '../../views/nav-menu/NavMenuSubmenuListItem';
import { useObservableBadgeValue } from '../../hooks/observable/useObservableBadgeValue';
import { formatMenuItemUrl } from '../../routing/route-utils';
import { Badge } from './Badge';

interface MobileMenuLinkProps {
  /** The nav menu item to render */
  menuItem: NavMenuItemType;
  /** Called after navigation so the drawer can close */
  onNavigate: () => void;
  /** MUI class name for the mobile badge styling */
  badgeClassName?: string;
  /** Optional label override (e.g. for buyerSpaceUsageRedesign renaming) */
  labelOverride?: string;
  /** Extra CSS className for the ListItem (e.g. login button styling) */
  className?: string;
}

/**
 * Mobile drawer equivalent of NavMenuLink.
 *
 * Each instance owns its own `useObservableBadgeValue` subscription so that
 * badge count changes only re-render the affected menu item — not the entire
 * nav tree.
 */
const MobileMenuLink: React.FC<MobileMenuLinkProps> = ({
  menuItem,
  onNavigate,
  badgeClassName,
  labelOverride,
  className,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const badgeValue = useObservableBadgeValue(menuItem);

  const handleClick = () => {
    onNavigate();

    const url = formatMenuItemUrl(menuItem, badgeValue);

    if (menuItem.external) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(url);

    if (menuItem.label === 'How It Works') {
      const element = document.getElementById('how-it-works');
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isActive = location.pathname === `/${menuItem.path}`;
  const highlight = isActive ? 'ss-drawer-menu-item-highlighted' : 'ss-drawer-menu-item-unhighlighted';

  return (
    <ListItem
      className={`ss-drawer-button ${highlight} ${className ?? ''}`}
      button
      onClick={handleClick}
    >
      <ListItemText primary={labelOverride ?? menuItem.label} />
      <Badge
        type="mobile"
        mobileClassName={badgeClassName}
        menuItem={menuItem}
      />
    </ListItem>
  );
};

export { MobileMenuLink };
